import { useState, useEffect } from "react";
import { FileList } from "./components/FileList";
import { FileUploadButton } from "./components/FileUploadButton";
import { useFileUpload } from "./hooks/useFileUpload";
import { toast } from "sonner";
import type { ExistingFile } from "./types";

interface FileUploaderProps {
  files: File[];
  setFiles: (files: File[]) => void;
  onFileError: (message: string) => void;
  isUploading?: boolean;
  existingFiles?: ExistingFile[];
  onDeleteFile?: (fileId: string) => Promise<void>;
  isLoading?: boolean;
  assistantId?: string;
  vectorStoreId?: string;
  onVectorStoreCreated?: (vectorStoreId: string) => void;
}

export const FileUploader = ({ 
  files, 
  setFiles, 
  onFileError, 
  isUploading = false,
  existingFiles = [],
  onDeleteFile,
  isLoading = false,
  assistantId,
  vectorStoreId,
  onVectorStoreCreated
}: FileUploaderProps) => {
  const [isDeletingFile, setIsDeletingFile] = useState<string | null>(null);
  const [localExistingFiles, setLocalExistingFiles] = useState<ExistingFile[]>(existingFiles);

  const {
    isUploadingNewFile,
    handleFileChange,
    ALLOWED_FILE_TYPES
  } = useFileUpload(assistantId, vectorStoreId, onVectorStoreCreated, setLocalExistingFiles);

  useEffect(() => {
    if (existingFiles?.length > 0) {
      console.log('Existing files updated:', existingFiles);
      setLocalExistingFiles(existingFiles);
    }
  }, [existingFiles]);

  const handleDeleteExistingFile = async (fileId: string) => {
    if (!onDeleteFile) return;
    
    try {
      setIsDeletingFile(fileId);
      await onDeleteFile(fileId);
    } catch (error: any) {
      console.error('Error deleting file:', error);
      toast.error(error.message || "Failed to delete file");
    } finally {
      setIsDeletingFile(null);
    }
  };

  return (
    <div className="space-y-4">
      <FileUploadButton
        onFileChange={handleFileChange}
        isUploading={isUploadingNewFile}
        disabled={isUploadingNewFile}
        accept=".py,.js,.md,.txt,.json,.pdf"
      />

      {localExistingFiles.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium">Existing Files</h4>
          <FileList
            files={localExistingFiles}
            onDeleteFile={handleDeleteExistingFile}
            isDeletingFile={isDeletingFile}
            isLoading={isLoading}
          />
        </div>
      )}
    </div>
  );
};